import React, { useState, useEffect } from 'react';
import { 
  FolderPlus, Plus, X, Globe, Lock, Edit3, Trash2, 
  ChevronRight, Bookmark, MoreHorizontal, ArrowLeft, ChefHat
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { RecipeCard } from './RecipeCard';

export const Collections = ({ userId, recipes, onSelectCollection, onOpenRecipe }) => {
  const [collections, setCollections] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [newCollection, setNewCollection] = useState({ name: '', description: '', is_public: false });
  const [isLoading, setIsLoading] = useState(true);
  const [viewingCollection, setViewingCollection] = useState(null);
  const [collectionRecipes, setCollectionRecipes] = useState([]);
  const [isLoadingRecipes, setIsLoadingRecipes] = useState(false);

  useEffect(() => {
    if (userId) fetchCollections();
  }, [userId]);

  const fetchCollections = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('collections')
        .select(`
          *,
          collection_recipes (recipe_id)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (!error && data) {
        setCollections(data.map(c => ({
          ...c,
          recipe_count: c.collection_recipes?.length || 0
        })));
      }
    } catch (e) {
      console.error('Error fetching collections:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCollectionRecipes = async (collection) => {
    setIsLoadingRecipes(true);
    setViewingCollection(collection);
    
    try {
      const { data: recipeLinks, error } = await supabase
        .from('collection_recipes')
        .select('recipe_id')
        .eq('collection_id', collection.id);

      if (!error && recipeLinks) {
        const recipeIds = recipeLinks.map(r => r.recipe_id);
        // Filter recipes from the passed recipes prop
        const filteredRecipes = recipes?.filter(r => recipeIds.includes(r.id)) || [];
        setCollectionRecipes(filteredRecipes);
      }
    } catch (e) {
      console.error('Error fetching collection recipes:', e);
    } finally {
      setIsLoadingRecipes(false);
    }
  };

  const handleCreate = async () => {
    if (!newCollection.name.trim()) return;
    
    try {
      const { data, error } = await supabase
        .from('collections')
        .insert({
          user_id: userId,
          name: newCollection.name.trim(),
          description: newCollection.description.trim(),
          is_public: newCollection.is_public
        })
        .select()
        .single();

      if (!error && data) {
        setCollections([{ ...data, recipe_count: 0 }, ...collections]);
        setNewCollection({ name: '', description: '', is_public: false });
        setIsCreating(false);
      }
    } catch (e) {
      console.error('Error creating collection:', e);
    }
  };

  const handleUpdate = async () => {
    if (!editingCollection || !editingCollection.name.trim()) return;
    
    try {
      const { error } = await supabase
        .from('collections')
        .update({
          name: editingCollection.name.trim(),
          description: editingCollection.description?.trim() || '',
          is_public: editingCollection.is_public
        })
        .eq('id', editingCollection.id);

      if (!error) {
        setCollections(collections.map(c => 
          c.id === editingCollection.id ? { ...c, ...editingCollection } : c
        ));
        setEditingCollection(null);
      }
    } catch (e) {
      console.error('Error updating collection:', e);
    }
  };

  const handleDelete = async (collectionId) => {
    if (!window.confirm('¿Eliminar esta colección? Las recetas no se eliminarán.')) return;
    
    try {
      // First delete all recipe associations
      await supabase
        .from('collection_recipes')
        .delete()
        .eq('collection_id', collectionId);
      
      // Then delete the collection
      const { error } = await supabase
        .from('collections')
        .delete()
        .eq('id', collectionId);

      if (!error) {
        setCollections(collections.filter(c => c.id !== collectionId));
      }
    } catch (e) {
      console.error('Error deleting collection:', e);
    }
  };

  const handleRemoveFromCollection = async (recipeId) => {
    if (!viewingCollection) return;
    
    try {
      await supabase
        .from('collection_recipes')
        .delete()
        .eq('collection_id', viewingCollection.id)
        .eq('recipe_id', recipeId);
      
      setCollectionRecipes(collectionRecipes.filter(r => r.id !== recipeId));
      // Update collection count
      setCollections(collections.map(c => 
        c.id === viewingCollection.id 
          ? { ...c, recipe_count: Math.max(0, c.recipe_count - 1) }
          : c
      ));
    } catch (e) {
      console.error('Error removing recipe from collection:', e);
    }
  };

  const CollectionCard = ({ collection }) => {
    const [showMenu, setShowMenu] = useState(false);

    return (
      <div 
        className="bg-white dark:bg-stone-900 rounded-[32px] border border-stone-100 dark:border-stone-800 p-6 hover:shadow-lg transition-all group cursor-pointer relative"
        onClick={() => fetchCollectionRecipes(collection)}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
            <Bookmark size={24} />
          </div>
          
          <div className="relative">
            <button 
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
              className="p-2 rounded-xl text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 transition-all"
            >
              <MoreHorizontal size={18} />
            </button>
            
            {showMenu && (
              <div className="absolute right-0 top-10 bg-white dark:bg-stone-900 rounded-2xl shadow-xl border border-stone-100 dark:border-stone-800 py-2 z-20 min-w-[140px]">
                <button 
                  onClick={(e) => { e.stopPropagation(); setEditingCollection(collection); setShowMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm font-bold text-stone-600 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 flex items-center gap-2"
                >
                  <Edit3 size={14} /> Editar
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); handleDelete(collection.id); setShowMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm font-bold text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 flex items-center gap-2"
                >
                  <Trash2 size={14} /> Eliminar
                </button>
              </div>
            )}
          </div>
        </div>
        
        <h3 className="text-lg font-black text-stone-900 dark:text-white mb-1 heading-serif">
          {collection.name}
        </h3>
        
        {collection.description && (
          <p className="text-sm text-stone-500 dark:text-stone-400 mb-4 line-clamp-2">
            {collection.description}
          </p>
        )}
        
        <div className="flex items-center justify-between pt-4 border-t border-stone-50 dark:border-stone-800">
          <div className="flex items-center gap-2">
            {collection.is_public ? (
              <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1.5 rounded-lg">
                <Globe size={12} /> Pública
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-stone-500 bg-stone-100 dark:bg-stone-800 px-3 py-1.5 rounded-lg">
                <Lock size={12} /> Privada
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-black uppercase tracking-widest text-stone-400">
              {collection.recipe_count} {collection.recipe_count === 1 ? 'receta' : 'recetas'}
            </span>
            <ChevronRight size={16} className="text-stone-300 group-hover:text-primary transition-colors" />
          </div>
        </div>
      </div>
    );
  };

  // View for collection recipes
  if (viewingCollection) {
    return (
      <div data-testid="collection-recipes-view" className="space-y-8 animate-in fade-in duration-500">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => {
              setViewingCollection(null);
              setCollectionRecipes([]);
            }}
            className="p-3 rounded-2xl bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 hover:bg-primary hover:text-white transition-all"
          >
            <ArrowLeft size={20} />
          </button>
          
          <div className="flex-1">
            <h3 className="text-2xl font-black heading-serif dark:text-white flex items-center gap-3">
              <Bookmark size={24} className="text-primary" />
              {viewingCollection.name}
            </h3>
            {viewingCollection.description && (
              <p className="text-stone-500 text-sm mt-1">{viewingCollection.description}</p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {viewingCollection.is_public ? (
              <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1.5 rounded-lg">
                <Globe size={12} /> Pública
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-stone-500 bg-stone-100 dark:bg-stone-800 px-3 py-1.5 rounded-lg">
                <Lock size={12} /> Privada
              </span>
            )}
          </div>
        </div>

        {isLoadingRecipes ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-72 bg-stone-100 dark:bg-stone-800 rounded-[32px] animate-pulse" />
            ))}
          </div>
        ) : collectionRecipes.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {collectionRecipes.map(recipe => (
              <div key={recipe.id} className="relative group">
                <RecipeCard 
                  recipe={recipe} 
                  onOpen={(r) => onOpenRecipe && onOpenRecipe(r)}
                  onLike={() => {}}
                  isLiked={false}
                  settings={{}}
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFromCollection(recipe.id);
                  }}
                  className="absolute top-4 right-4 p-2 bg-rose-500 text-white rounded-xl opacity-0 group-hover:opacity-100 transition-all shadow-lg hover:bg-rose-600 z-10"
                  title="Quitar de la colección"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-stone-50 dark:bg-stone-900/50 rounded-[40px] border-2 border-dashed border-stone-100 dark:border-stone-800">
            <ChefHat size={48} className="mx-auto mb-4 text-stone-200" />
            <p className="text-stone-500 font-bold mb-2">Esta colección está vacía</p>
            <p className="text-stone-400 text-sm">Añade recetas desde la página de detalle de cada receta</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="collections-section" className="space-y-8">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-black heading-serif dark:text-white flex items-center gap-3">
          <FolderPlus size={24} className="text-primary" />
          Mis Colecciones
        </h3>
        
        <button 
          data-testid="create-collection-btn"
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 px-5 py-3 bg-primary text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-lg shadow-primary/20 hover:scale-105 transition-transform"
        >
          <Plus size={16} /> Nueva
        </button>
      </div>

      {/* Create/Edit Modal */}
      {(isCreating || editingCollection) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-stone-900/60 backdrop-blur-md">
          <div className="bg-white dark:bg-stone-900 w-full max-w-md rounded-[40px] p-8 shadow-2xl animate-in zoom-in-95">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-xl font-black heading-serif dark:text-white">
                {editingCollection ? 'Editar Colección' : 'Nueva Colección'}
              </h3>
              <button 
                onClick={() => { setIsCreating(false); setEditingCollection(null); }}
                className="p-2 rounded-xl text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-stone-400 mb-2">
                  Nombre
                </label>
                <input 
                  data-testid="collection-name-input"
                  type="text"
                  value={editingCollection ? editingCollection.name : newCollection.name}
                  onChange={(e) => editingCollection 
                    ? setEditingCollection({ ...editingCollection, name: e.target.value })
                    : setNewCollection({ ...newCollection, name: e.target.value })
                  }
                  placeholder="Ej: Meal Prep Semanal"
                  className="w-full p-4 rounded-2xl bg-stone-50 dark:bg-stone-950 border border-stone-100 dark:border-stone-800 outline-none font-bold dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-stone-400 mb-2">
                  Descripción (opcional)
                </label>
                <textarea 
                  value={editingCollection ? editingCollection.description || '' : newCollection.description}
                  onChange={(e) => editingCollection 
                    ? setEditingCollection({ ...editingCollection, description: e.target.value })
                    : setNewCollection({ ...newCollection, description: e.target.value })
                  }
                  placeholder="Recetas para preparar el domingo..."
                  className="w-full p-4 h-24 rounded-2xl bg-stone-50 dark:bg-stone-950 border border-stone-100 dark:border-stone-800 outline-none font-medium text-sm dark:text-white resize-none"
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-stone-50 dark:bg-stone-950 rounded-2xl">
                <div className="flex items-center gap-3">
                  {(editingCollection?.is_public ?? newCollection.is_public) ? (
                    <Globe size={18} className="text-emerald-500" />
                  ) : (
                    <Lock size={18} className="text-stone-400" />
                  )}
                  <div>
                    <p className="text-sm font-bold dark:text-white">
                      {(editingCollection?.is_public ?? newCollection.is_public) ? 'Pública' : 'Privada'}
                    </p>
                    <p className="text-[10px] text-stone-400">
                      {(editingCollection?.is_public ?? newCollection.is_public) 
                        ? 'Otros usuarios pueden verla' 
                        : 'Solo tú puedes verla'}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => editingCollection 
                    ? setEditingCollection({ ...editingCollection, is_public: !editingCollection.is_public })
                    : setNewCollection({ ...newCollection, is_public: !newCollection.is_public })
                  }
                  className={`w-12 h-7 rounded-full transition-all relative ${
                    (editingCollection?.is_public ?? newCollection.is_public) ? 'bg-emerald-500' : 'bg-stone-200 dark:bg-stone-700'
                  }`}
                >
                  <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all shadow-md ${
                    (editingCollection?.is_public ?? newCollection.is_public) ? 'left-6' : 'left-1'
                  }`} />
                </button>
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <button 
                onClick={() => { setIsCreating(false); setEditingCollection(null); }}
                className="flex-1 py-4 bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 rounded-2xl font-black uppercase tracking-widest text-[10px]"
              >
                Cancelar
              </button>
              <button 
                data-testid="save-collection-btn"
                onClick={editingCollection ? handleUpdate : handleCreate}
                className="flex-1 py-4 bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-xl"
              >
                {editingCollection ? 'Guardar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Collections Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-48 bg-stone-100 dark:bg-stone-800 rounded-[32px] animate-pulse" />
          ))}
        </div>
      ) : collections.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {collections.map(collection => (
            <CollectionCard key={collection.id} collection={collection} />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-stone-50 dark:bg-stone-900/50 rounded-[40px] border-2 border-dashed border-stone-100 dark:border-stone-800">
          <FolderPlus size={48} className="mx-auto mb-4 text-stone-200" />
          <p className="text-stone-500 font-bold mb-2">No tienes colecciones aún</p>
          <p className="text-stone-400 text-sm mb-6">Crea tu primera colección para organizar tus recetas favoritas</p>
          <button 
            onClick={() => setIsCreating(true)}
            className="px-6 py-3 bg-primary text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-lg"
          >
            Crear mi primera colección
          </button>
        </div>
      )}
    </div>
  );
};

export default Collections;
